// booking-system-react-web
// Single-file frontend (React) suitable for web bundlers (Rollup, Vite, webpack).

import React, { useEffect, useMemo, useState, useRef } from 'react';
import * as style from "./App.module.css"
import './Common.module.css'
import { InfoModal, WarningModal, ErrorModal } from './Modals.jsx'
import { SegmentedControl } from './listItems.jsx'
import { ThemeContext } from './Context.jsx'
import { Calendar } from './Calendar.jsx'
import { PrecompiledForm } from './Forms.jsx'
import { themeDefaults } from './Defaults.jsx'

// Utilities
const pad = (n) => String(n).padStart(2, '0');
const yyyyMm = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const dateIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const API_ORIGIN = 'https://api.it4ll.eu/business/booker';

export default function App() {
  // token handling: read from ?t= token if in browser
  const [token, setToken] = useState(() => {
    try {
      if (typeof window === 'undefined') return null;
      const p = new URL(window.location.href).searchParams;
      return p.get('t') || 'ERROR-TOKEN'; // fallback demo token for local dev
    } catch (e) {
      return 'ERROR-TOKEN';
    }
  });

  const today = useMemo(() => new Date(), []);
  const [cursorMonthIndex, setCursorMonthIndex] = useState(0); // 0..2
  const [calendarMeta, setCalendarMeta] = useState(null);
  const [data, setData] = useState({});
  const [calLoading, setCalLoading] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingData, setBookingData] = useState({ name: '', lastname: '', email: '', phone: '', company: '' });
  const [justBooked, setJustBooked] = useState(null);
  const [days, setDays] = useState([]);
  const [overlay, setOverlay] = useState("none");
  const [activeSlot, setActiveSlot] = useState(-1);
  const [theme, setTheme] = useState(themeDefaults)
  const [showInfo, setShowInfo] = useState(false)
  const refClientInfo = useRef();
  const [ version, setVersion ] = useState("remote");

  const viewMonth = useMemo(() => new Date(today.getFullYear(), today.getMonth() + cursorMonthIndex, 1), [today, cursorMonthIndex]);

  useEffect(() => {
    if (!token) return;
    const url_events = new URL(`${API_ORIGIN}/calendar/${encodeURIComponent(token)}/events`);
    const url_meta = new URL(`${API_ORIGIN}/calendar/${encodeURIComponent(token)}/meta`);
    fetch(url_meta.toString()).then((res)=>res.json()).then((resp)=>{
      let { name, lastname, email, address, postalcode, city, country, phone } = resp.message;
      if(resp.message && resp.message.length > 0) setCalendarMeta(resp.message[0]);
      fetch(url_events.toString()).then((res)=>res.json()).then((_data)=>{
        setData(_data.message)
        setDays(Object.keys(_data.message))
      }).catch((err)=> { console.error(err); setOverlay("error fetching events") });
    }).catch((err)=> { console.error(err); setOverlay("error fetching user meta") });
  }, [token]);

  const submitBooking = async (formValues) => {
    console.log(formValues)
    console.log(bookingData)
    const body = {
      euuid: bookingData.euuid,
      date: dateIso(bookingData.date),
      type: version,
      slot: bookingData.slot,
      name: formValues.name,
      lastname:  formValues.lastname,
      email: formValues.email,
      phone: formValues.phone,
      company:  formValues.company,
    };
    try {
      const r = await fetch(`${API_ORIGIN}/book/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error('booking failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      setJustBooked({ downloadUrl: url, details: body });
      setShowBookingForm(false);
      alert('Booked — click Download to add to your calendar.');
    } catch (err) {
      console.error('booking error', err);
      alert('Could not complete booking.');
    }
  };

  return (
    <ThemeContext.Provider value={[ theme, setTheme ]}>
    <div style={{ display: "block", width: "100%", height: "100%", position: "relative"}}>
      { overlay === "error" ? <ErrorModal onAccept={() =>  {setOverlay("none")}} /> : <></>}
      <div className={style.main_cont} style={{ color: theme.stxt }}>
        {/* HEADER section */}
        <div className={style.header_cont}>
          <div className={style.header}>
            <div style={{ padding: '0% 3%' }}>
              <img src="./assets/logo_it4ll_simple_512x512.png" alt="IT4LL logo" width={96} height={96} />
            </div>
            <div style={{width: "100%", display: "flex", flexDirection: "row", justifyContent: 'end'}}>
              <span className="title" style={{textAlign: "right", color: theme.stxt }}>Book a meeting</span>
              <div className={style.meetingTypeSelector} style={{ flex: 1 }}>
                <SegmentedControl categories={["remote", "local"]} onSelectCategory={(i)=> { setVersion(i) }} />
              </div>
            </div>
          </div>
        </div>

        <div className={style.booking_cont}>
          <div className={style.booking}>
            <div className={style.page} style={{backgroundColor: theme.bg}}>
              <Calendar
                key={version}
                avail={data}
                days={days}
                onSetBookingData={(bd)=>{ setBookingData(bd); setShowBookingForm(true); setShowInfo(true) }}
              />
            </div>
            <div
              className={style.page}
              ref={refClientInfo}
              style={{ transform: showBookingForm ? 'translateX(-100%)' : 'translate(5%)', backgroundColor: theme.bg }}
            >
              <PrecompiledForm
                dtime={bookingData}
                title={"Your Booking"}
                items={calendarMeta}
                onDelete={()=>{ setShowBookingForm(false); setShowInfo(false) }}
                onSubmit={(values)=>submitBooking(values)}
                shown={showInfo}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </ThemeContext.Provider>
  );
}
